import { useState, useCallback } from 'react';
import { formatNumber, convertRateToMonthlyString } from 'utils';
import {
  RS_HOME_OWNER,
  RS_MORTGAGE,
  RS_BOARDING,
  RS_RENT,
  RS_LIVING_WITH_RELATIVE,
  RS_OTHER,
} from '_config/_constants';

export const useExpenseWarning = () => {
  const [warnings, setWarning] = useState([]);

  const check = useCallback((values, storeState) => {
    const { residentialStatus } = storeState.acquisition.otherDetails;

    const mortgageRentBoardMonthly = toMonthlyNumber(values.mortgageRentBoard);
    const mortgageRentBoardMonthlyDisplay = formatNumber(mortgageRentBoardMonthly);

    const newWarnings = [];

    const totalLivingExpenses = getTotalLivingExpenses(values);
    if (totalLivingExpenses === 0) {
      newWarnings.push(
        "It looks like you've entered $0 for your living expenses. Please review and update your input.",
      );
    }

    if (mortgageRentBoardMonthly === 0 && residentialStatus === RS_RENT) {
      newWarnings.push(
        `Please review and confirm your share of the monthly rent is $${mortgageRentBoardMonthlyDisplay}.`,
      );
    }

    if (mortgageRentBoardMonthly > 15000) {
      switch (residentialStatus) {
        case RS_MORTGAGE:
          newWarnings.push(
            `Please review and confirm your share of the monthly mortgage repayments is $${mortgageRentBoardMonthlyDisplay}.`,
          );
          break;
        case RS_HOME_OWNER:
        case RS_OTHER:
        case RS_LIVING_WITH_RELATIVE:
          newWarnings.push(
            `Please review and confirm your share of the monthly housing expenses is $${mortgageRentBoardMonthlyDisplay}.`,
          );
          break;
        case RS_BOARDING:
          newWarnings.push(
            `Please review and confirm your share of the monthly board is $${mortgageRentBoardMonthlyDisplay}.`,
          );
          break;
        case RS_RENT:
          newWarnings.push(
            `Please review and confirm your share of the monthly rent is $${mortgageRentBoardMonthlyDisplay}.`,
          );
          break;
        default:
          throw new Error(`Unexpected residential status: ${residentialStatus}`);
      }
    }

    setWarning(newWarnings);
    return newWarnings.length === 0;
  }, []);

  return { check, warnings };
};

function getTotalLivingExpenses(values) {
  const fields =
    values.isSummaryExpenses === 'yes'
      ? [values.basicExpenses, values.additionalExpenses]
      : [
          values.foodGroceriesLifestyleEntertainment,
          values.insurance,
          values.utilities,
          values.educationAndChildcare,
          values.transport,
          values.personalAndOtherExpense,
        ];

  return fields.reduce((acc, field) => acc + toMonthlyNumber(field), 0);
}

function toMonthlyNumber(value) {
  return Number(convertRateToMonthlyString(value));
}
